import { useEffect, useState } from "react";
import "@/App.css";
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  HeartPulse,
  Activity,
  Stethoscope,
  Zap,
  ShieldCheck,
  ArrowUpRight,
  Menu,
  X,
  Mail,
} from "lucide-react";

const ASSETS = {
  doctor:
    "https://customer-assets.emergentagent.com/job_031fd1b9-274a-49ce-a282-aa7e7581dcee/artifacts/yofj4gye_Dr%20Pratik%20Sane.png",
  logoVertical:
    "https://customer-assets.emergentagent.com/job_031fd1b9-274a-49ce-a282-aa7e7581dcee/artifacts/yy6b30m6_The%20Sane%20Heart%20Clinic%20-%20Logos%2005%20Coloured%20Logo%20Copy%202.png",
  logoHorizontal:
    "https://customer-assets.emergentagent.com/job_cardiac-care-thane/artifacts/sbxifujn_The%20Sane%20Heart%20Clinic%20-%20Logos%2006%20Coloured%20Logo%20Copy%203%20%281%29.png",
  logomark:
    "https://customer-assets.emergentagent.com/job_031fd1b9-274a-49ce-a282-aa7e7581dcee/artifacts/q7d7ewso_The%20Sane%20Heart%20Clinic%20Logomark%2001%20Artboard%2012.png",
};

const PHONE_DISPLAY = "+91 98334 53466";
const PHONE_TEL = "+919833453466";
const WHATSAPP_URL = `https://wa.me/919833453466?text=${encodeURIComponent(
  "Hello Dr. Sane, I would like to book an appointment at The Sane Heart Clinic."
)}`;
const ADDRESS_LINE_1 = "Office Number 5, First Floor, Glorio";
const ADDRESS_LINE_2 = "Puranik's Grand Central, Pokhran Road 1";
const ADDRESS_LINE_3 = "Vartak Nagar, Thane (W) – 400606";
const MAPS_QUERY = encodeURIComponent(
  "Glorio, Puranik's Grand Central, Pokhran Road 1, Vartak Nagar, Thane West 400606"
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "doctor", label: "Doctor", testid: "nav-link-doctor" },
    { id: "services", label: "Services", testid: "nav-link-services" },
    { id: "hours", label: "Hours", testid: "nav-link-hours" },
    { id: "contact", label: "Contact", testid: "nav-link-contact" },
  ];
  return (
    <header
      className="fixed top-0 left-0 w-full z-50 bg-[#EEEAD9]/85 backdrop-blur-xl border-b border-[#CDBF9C]/60"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-28 sm:h-32 lg:h-36">
        <a
          href="#top"
          className="flex items-center"
          data-testid="nav-logo"
        >
          <img
            src={ASSETS.logoHorizontal}
            alt="the SANE heart clinic"
            className="h-24 sm:h-28 lg:h-32 w-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="font-sans text-sm tracking-wide text-[#2E1A19] hover:text-[#692927] transition-colors duration-300 relative group"
              data-testid={l.testid}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#692927] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            className="hidden sm:inline-flex items-center justify-center bg-[#692927] text-[#EEEAD9] hover:bg-[#4F1E1C] transition-colors duration-300 px-5 py-2.5 text-sm font-medium gap-2"
            data-testid="nav-cta-book"
          >
            <Phone className="w-4 h-4" />
            Book Appointment
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#692927] p-2"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#CDBF9C]/60 bg-[#EEEAD9]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="font-sans text-base text-[#2E1A19] hover:text-[#692927]"
                data-testid={`mobile-${l.testid}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center justify-center bg-[#692927] text-[#EEEAD9] px-5 py-3 text-sm font-medium gap-2"
            >
              <Phone className="w-4 h-4" /> Book Appointment
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-36 lg:pt-44 pb-16 lg:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left: Logo + headline */}
        <div className="lg:col-span-7 reveal">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#692927]" />
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
              Interventional Cardiology · Thane
            </span>
          </div>

          <h1
            className="font-serif text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] tracking-tight text-[#692927]"
            data-testid="hero-title"
          >
            Expert cardiac
            <br />
            care, with a
            <br />
            <span className="italic font-medium">human touch.</span>
          </h1>

          <p className="font-sans text-lg lg:text-xl leading-relaxed text-[#2E1A19]/85 mt-8 max-w-xl">
            the SANE heart clinic is led by Dr. Pratik S. Sane — a consultant
            and interventional cardiologist offering thoughtful diagnosis,
            modern interventions and long-term care for the heart.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-10">
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center justify-center bg-[#692927] text-[#EEEAD9] hover:bg-[#4F1E1C] transition-colors duration-300 px-7 py-4 text-base font-medium gap-2 group"
              data-testid="hero-cta-call"
            >
              <Phone className="w-4 h-4" />
              Call {PHONE_DISPLAY}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center bg-transparent text-[#692927] hover:bg-[#CDBF9C]/30 transition-colors duration-300 px-7 py-4 text-base font-medium gap-2 border border-[#692927]"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Right: Doctor portrait inside arch */}
        <div className="lg:col-span-5 relative reveal reveal-delay-2">
          <div className="relative w-full aspect-[4/5] max-w-md mx-auto">
            {/* Background offset block */}
            <div className="absolute -inset-6 bg-[#CDBF9C]/55 rounded-t-[200px] rounded-b-md -z-10" />
            {/* Image frame (arch) */}
            <div className="relative w-full h-full overflow-hidden rounded-t-[200px] rounded-b-md border border-[#CDBF9C]">
              <img
                src={ASSETS.doctor}
                alt="Dr. Pratik S. Sane"
                className="w-full h-full object-cover"
                data-testid="hero-doctor-image"
              />
            </div>
            {/* Floating credential card */}
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-[#EEEAD9] border border-[#CDBF9C] px-5 py-4 shadow-[0_12px_40px_rgba(105,41,39,0.08)]">
              <div className="font-sans text-[0.65rem] uppercase tracking-[0.25em] text-[#692927] font-semibold mb-1">
                Consultant
              </div>
              <div className="font-serif text-xl text-[#2E1A19] leading-tight">
                Dr. Pratik S. Sane
              </div>
              <div className="font-sans text-xs text-[#5A3E3D] mt-0.5">
                Interventional Cardiologist
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Doctor = () => {
  const credentials = [
    "Graduation (MBBS) — Pravara Institute of Medical Sciences, Loni",
    "Post-graduation in General Medicine — Deenanath Mangeshkar Hospital, Pune",
    "Super-speciality training in Cardiology — Madras Medical Mission, Chennai",
    "Special training in Transcatheter Heart Procedures such as TAVI",
    "Two-year Fellowship in Interventional Cardiology — Narayana Hrudayalaya, Bengaluru",
    "Published author in peer-reviewed national & international journals",
  ];

  return (
    <section
      id="doctor"
      className="relative py-24 sm:py-32 border-t border-[#CDBF9C]/60"
      data-testid="doctor-bio-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
            01 — Know Your Doctor
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#692927] mt-6">
            Meet Dr. Pratik S. Sane
          </h2>
          <div className="mt-8 hidden lg:block opacity-50">
            <img
              src={ASSETS.logomark}
              alt=""
              className="w-32 h-32 object-contain"
            />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div data-testid="doctor-credentials-list">
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
              Training & Credentials
            </span>
            <ul className="mt-6 space-y-5">
              {credentials.map((c, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-[#CDBF9C]/70 pb-5 last:border-0">
                  <span className="font-serif text-2xl text-[#692927] leading-none mt-0.5">
                    ›
                  </span>
                  <span className="font-sans text-lg text-[#2E1A19]/90 leading-relaxed">
                    {c}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const conditions = [
    {
      icon: Activity,
      title: "Hypertension",
      desc: "Comprehensive evaluation and long-term management of high blood pressure.",
    },
    {
      icon: HeartPulse,
      title: "Heart Failure",
      desc: "Modern protocols for chronic and acute heart failure with quality-of-life focus.",
    },
    {
      icon: Zap,
      title: "Coronary Artery Disease",
      desc: "Diagnosis and treatment of heart attacks, blockages and chronic CAD.",
    },
    {
      icon: ShieldCheck,
      title: "Valve Diseases",
      desc: "Assessment and care for stenosis, regurgitation and rheumatic valve disease.",
    },
    {
      icon: Stethoscope,
      title: "Heart Muscle Disorders",
      desc: "Cardiomyopathies and complex muscle disorders managed with care.",
    },
    {
      icon: HeartPulse,
      title: "Structural Heart Interventions",
      desc: "Special interest in adult structural heart procedures.",
    },
  ];

  const interventions = [
    {
      label: "Coronary Angiography",
      desc: null,
    },
    {
      label: "Angioplasty",
      desc: null,
    },
    {
      label: "Pacemakers / AICD",
      desc: null,
    },
    {
      label: "Transcatheter Procedures",
      desc: "Balloon mitral valvotomy (PTMC/BMV), ASD and PFO device closures, TAVI / TAVR (Transcatheter Aortic Valve Interventions) and other adult structural heart interventions.",
      wide: true,
    },
  ];

  const clinicServices = [
    { icon: Activity, label: "ECG" },
    { icon: HeartPulse, label: "2D Echo" },
    { icon: Zap, label: "Stress Test" },
    { icon: ShieldCheck, label: "Vaccinations" },
  ];

  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 bg-[#CDBF9C]/25 border-y border-[#CDBF9C]/60"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
              02 — Services & Conditions
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#692927] mt-6">
              Care that covers
              <br />
              every chamber of the heart.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="font-sans text-lg leading-relaxed text-[#2E1A19]/85">
              From everyday cardiac concerns to advanced interventional
              procedures, the clinic offers a continuum of care under one roof —
              with a focus on accurate diagnosis, conservative first principles
              and skilled intervention only when warranted.
            </p>
          </div>
        </div>

        {/* Conditions grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#CDBF9C]"
          data-testid="services-grid"
        >
          {conditions.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={i}
                className="bg-[#EEEAD9] p-8 lg:p-10 transition-all duration-300 hover:bg-[#692927] group"
                data-testid="service-card"
              >
                <Icon className="w-8 h-8 text-[#692927] group-hover:text-[#CDBF9C] transition-colors duration-300" />
                <h3 className="font-serif text-2xl text-[#2E1A19] group-hover:text-[#EEEAD9] transition-colors duration-300 mt-6">
                  {c.title}
                </h3>
                <p className="font-sans text-sm leading-relaxed text-[#5A3E3D] group-hover:text-[#EEEAD9]/85 transition-colors duration-300 mt-3">
                  {c.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Interventions ribbon */}
        <div className="mt-16 border border-[#CDBF9C] bg-[#EEEAD9] p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
                Interventional Procedures
              </span>
              <p className="font-serif text-2xl text-[#2E1A19] mt-3 leading-snug">
                Diagnostic & therapeutic excellence.
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {interventions.map((it, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-4 border-b border-[#CDBF9C]/70 pb-4 ${
                      it.wide ? "sm:col-span-2" : ""
                    }`}
                  >
                    <span className="font-serif text-3xl text-[#692927] leading-none mt-1">
                      0{i + 1}
                    </span>
                    <div className="flex-1">
                      <div className="font-sans text-base font-medium text-[#2E1A19]">
                        {it.label}
                      </div>
                      {it.desc && (
                        <div className="font-sans text-sm leading-relaxed text-[#5A3E3D] mt-2">
                          {it.desc}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Clinic Services */}
        <div className="mt-10 border border-[#CDBF9C] bg-[#EEEAD9] p-8 lg:p-12" data-testid="clinic-services-block">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
                Clinic Services
              </span>
              <p className="font-serif text-2xl text-[#2E1A19] mt-3 leading-snug">
                In-clinic diagnostics, on the same visit.
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#CDBF9C]" data-testid="clinic-services-grid">
                {clinicServices.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <li
                      key={i}
                      className="bg-[#EEEAD9] flex flex-col items-start gap-4 p-6 transition-colors duration-300 hover:bg-[#CDBF9C]/30"
                      data-testid="clinic-service-card"
                    >
                      <Icon className="w-7 h-7 text-[#692927]" />
                      <span className="font-serif text-xl text-[#2E1A19]">
                        {s.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Hours = () => {
  return (
    <section
      id="hours"
      className="relative py-24 sm:py-32"
      data-testid="hours-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#692927] font-semibold">
            03 — Clinic Hours
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#692927] mt-6">
            When we're
            <br />
            here for you.
          </h2>
          <p className="font-sans text-lg leading-relaxed text-[#2E1A19]/85 mt-6">
            Appointments are recommended to reduce wait times. For urgent
            cardiac concerns, please call directly — we'll do our best to
            accommodate you the same day.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            className="inline-flex items-center gap-2 mt-8 font-sans text-base text-[#692927] border-b border-[#692927] pb-1 hover:gap-3 transition-all"
          >
            <Clock className="w-4 h-4" /> Call to schedule
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="lg:col-span-7">
          <div
            className="border border-[#CDBF9C] bg-[#EEEAD9] p-10 lg:p-14"
            data-testid="contact-hours-table"
          >
            <div className="flex items-center gap-3 text-[#692927]">
              <Clock className="w-5 h-5" />
              <span className="font-sans text-xs uppercase tracking-[0.3em] font-semibold">
                Consulting Hours
              </span>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <span className="font-serif text-3xl sm:text-4xl text-[#2E1A19] leading-tight">
                Monday – Friday
              </span>
              <span className="font-serif text-5xl sm:text-6xl text-[#692927] tracking-tight leading-none">
                4:30 PM – 8:00 PM
              </span>
            </div>
            <div className="mt-10 pt-6 border-t border-[#CDBF9C]/70 flex items-center justify-end flex-wrap gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-sans text-sm text-[#692927] hover:gap-3 transition-all"
              >
                <MessageCircle className="w-4 h-4" /> Message on WhatsApp
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 bg-[#692927] text-[#EEEAD9]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: contact info */}
          <div className="lg:col-span-6">
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#CDBF9C] font-semibold">
              04 — Visit & Contact
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#EEEAD9] mt-6">
              Let's take care of
              <br />
              your heart, together.
            </h2>

            <div className="mt-12 space-y-10">
              <div>
                <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#CDBF9C] font-semibold">
                  Appointments
                </span>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="block font-serif text-3xl sm:text-4xl text-[#EEEAD9] mt-3 hover:text-[#CDBF9C] transition-colors"
                  data-testid="contact-phone"
                >
                  {PHONE_DISPLAY}
                </a>
              </div>

              <div>
                <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#CDBF9C] font-semibold">
                  Clinic Address
                </span>
                <address
                  className="not-italic font-sans text-lg leading-relaxed text-[#EEEAD9]/90 mt-3"
                  data-testid="contact-address"
                >
                  {ADDRESS_LINE_1}
                  <br />
                  {ADDRESS_LINE_2}
                  <br />
                  {ADDRESS_LINE_3}
                </address>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="inline-flex items-center justify-center bg-[#EEEAD9] text-[#692927] hover:bg-[#CDBF9C] transition-colors duration-300 px-7 py-4 text-base font-medium gap-2"
                  data-testid="contact-cta-call"
                >
                  <Phone className="w-4 h-4" />
                  Call Clinic
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center bg-transparent text-[#EEEAD9] hover:bg-[#EEEAD9]/10 transition-colors duration-300 px-7 py-4 text-base font-medium gap-2 border border-[#EEEAD9]"
                  data-testid="contact-cta-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Right: map */}
          <div className="lg:col-span-6">
            <div className="border border-[#CDBF9C]/40 overflow-hidden h-full min-h-[420px]">
              <iframe
                title="the SANE heart clinic Location"
                src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "420px", filter: "grayscale(0.2) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen=""
                data-testid="contact-map-embed"
              />
            </div>
            <a
              href={`https://www.google.com/maps?q=${MAPS_QUERY}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 font-sans text-sm text-[#CDBF9C] hover:text-[#EEEAD9] border-b border-[#CDBF9C]/60 pb-1"
              data-testid="contact-cta-directions"
            >
              <MapPin className="w-4 h-4" /> Get directions
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer
      className="bg-[#EEEAD9] border-t border-[#CDBF9C]/70 py-16"
      data-testid="footer-container"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
        <div className="flex items-center">
          <img
            src={ASSETS.logoHorizontal}
            alt="the SANE heart clinic"
            className="h-20 sm:h-24 w-auto object-contain"
          />
        </div>

        <div className="flex flex-col gap-2 md:items-center">
          <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#692927] font-semibold">
            Explore
          </span>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 md:justify-center">
            <a href="#doctor" className="font-sans text-sm text-[#2E1A19] hover:text-[#692927]">
              Doctor
            </a>
            <a href="#services" className="font-sans text-sm text-[#2E1A19] hover:text-[#692927]">
              Services
            </a>
            <a href="#hours" className="font-sans text-sm text-[#2E1A19] hover:text-[#692927]">
              Hours
            </a>
            <a href="#contact" className="font-sans text-sm text-[#2E1A19] hover:text-[#692927]">
              Contact
            </a>
          </div>
        </div>

        <div className="md:text-right space-y-2">
          <a
            href={`tel:${PHONE_TEL}`}
            className="flex md:justify-end items-center gap-2 font-sans text-sm text-[#2E1A19] hover:text-[#692927]"
          >
            <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
          </a>
          <p className="font-sans text-xs text-[#5A3E3D] leading-relaxed">
            © {new Date().getFullYear()} the SANE heart clinic. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Home = () => {
  useEffect(() => {
    document.title = "the SANE heart clinic · Dr. Pratik S. Sane — Cardiologist, Thane";
  }, []);

  return (
    <div className="min-h-screen bg-[#EEEAD9] text-[#2E1A19]">
      <Nav />
      <main>
        <Hero />
        <Doctor />
        <Services />
        <Hours />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Home />
    </div>
  );
}

export default App;
